import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  buildProductRow, commercialStatusFromPriority, computeCommercialScore,
  isAtRuptureRisk, resolveSafetyDays, DEFAULT_SAFETY_DAYS, type SafetyDaysConfig,
} from '../lib/replenishment'
import { buildFilialSuggestionMap, fetchFilialNeeds, type FilialSuggestion } from '../lib/filialNeeds'
import { REPLENISHMENT_REFRESH_EVENT } from './useCompanySafetyStock'
import type { OrderStatus, Product, ProductRow, PurchaseOrder, PurchaseOrderItem, Sale } from '../types'

interface ActiveOrderInfo {
  order_number: string
  created_at: string
  status: OrderStatus
}

const PAGE_SIZE = 1000

export function useReplenishment() {
  const [rows, setRows] = useState<ProductRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Quebra da necessidade das filiais por SKU (para tooltip e flag de exibição).
  const [filialMap, setFilialMap] = useState<Map<string, FilialSuggestion>>(new Map())
  const [hasFiliais, setHasFiliais] = useState(false)

  async function fetchAllProducts(): Promise<Product[]> {
    const all: Product[] = []
    let from = 0
    while (true) {
      const { data, error: err } = await supabase
        .from('products').select('*, suppliers(id, name, lead_time_days, min_order, payment_term_1, payment_term_2, payment_term_3, payment_term_4, payment_term_5, payment_term_6)')
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('name')
        .range(from, from + PAGE_SIZE - 1)
      if (err) throw err
      if (!data || data.length === 0) break
      all.push(...(data as Product[]))
      if (data.length < PAGE_SIZE) break
      from += PAGE_SIZE
    }
    return all
  }

  // Colchão anti-ruptura da empresa ativa (dias de segurança por curva ABC).
  // Best-effort: erro/coluna ausente (pré-migration) → DEFAULT (0/0/0 = comportamento
  // anterior), nunca derruba o painel. Mesmo caminho de useCompanySafetyStock.
  async function fetchSafetyConfig(): Promise<SafetyDaysConfig> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return DEFAULT_SAFETY_DAYS
      const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
      if (!profile?.company_id) return DEFAULT_SAFETY_DAYS
      const { data, error: err } = await supabase
        .from('companies')
        .select('safety_days_a, safety_days_b, safety_days_c')
        .eq('id', profile.company_id)
        .single()
      if (err || !data) return DEFAULT_SAFETY_DAYS
      return {
        a: Number(data.safety_days_a ?? 0),
        b: Number(data.safety_days_b ?? 0),
        c: Number(data.safety_days_c ?? 0),
      }
    } catch {
      return DEFAULT_SAFETY_DAYS
    }
  }

  async function fetchData() {
    try {
      // Janela de 12 meses cobre o cálculo de monthly_avg (usa últimos 4 meses).
      // lastSaleDate cai para product.last_movement_at se a venda for anterior à janela.
      const salesCutoff = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      const [products, salesRes, ordersRes, lastSaleRes, filialNeeds, safetyCfg] = await Promise.all([
        fetchAllProducts(),
        supabase.from('sales').select('product_id, quantity, sale_date').gte('sale_date', salesCutoff),
        supabase.from('purchase_orders')
          .select('id, order_number, created_at, status, purchase_order_items(product_id, quantity)')
          .in('status', ['Pendente', 'Enviado'])
          .order('created_at', { ascending: false }),
        // View de última venda (vendas puras, histórico completo) — usada só para o flag de encalhe,
        // mantendo a MESMA régua do dashboard/inativos. Best-effort: falha aqui não derruba a página.
        supabase.from('last_sale_per_product').select('product_id, last_sale_date'),
        // Necessidade das filiais vinculadas (vazio se a empresa ativa não for matriz).
        // Best-effort: erro aqui não derruba o painel — apenas zera a consolidação.
        // Colchão por curva na filial depende de a RPC expor a curva ABC (Fase 2); por ora 0.
        fetchFilialNeeds().catch(() => []),
        // Colchão anti-ruptura por curva ABC (Fase 1).
        fetchSafetyConfig(),
      ])

      if (salesRes.error)  { setError(salesRes.error.message);  setLoading(false); return }
      if (ordersRes.error) { setError(ordersRes.error.message); setLoading(false); return }

      const fMap = buildFilialSuggestionMap(filialNeeds)
      setFilialMap(fMap)
      setHasFiliais(fMap.size > 0)

      const sales = (salesRes.data ?? []) as Sale[]
      const orders = (ordersRes.data ?? []) as Array<PurchaseOrder & { purchase_order_items: Pick<PurchaseOrderItem, 'product_id' | 'quantity'>[] }>

      // Quantidade total em aberto (Pendente/Enviado) por produto → abate na sugestão e no score.
      const openQtyByProduct = new Map<string, number>()
      orders.forEach(o => o.purchase_order_items.forEach(it => {
        if (!it.product_id) return
        openQtyByProduct.set(it.product_id, (openQtyByProduct.get(it.product_id) ?? 0) + (Number(it.quantity) || 0))
      }))

      // Velocidade: unidades nos últimos 30d vs 30d anteriores → "venda acelerando".
      const now = Date.now()
      const cut30 = now - 30 * 86_400_000
      const cut60 = now - 60 * 86_400_000
      const last30 = new Map<string, number>()
      const prev30 = new Map<string, number>()
      for (const s of sales) {
        const t = new Date(s.sale_date).getTime()
        const q = Number(s.quantity) || 0
        if (t >= cut30) last30.set(s.product_id, (last30.get(s.product_id) ?? 0) + q)
        else if (t >= cut60) prev30.set(s.product_id, (prev30.get(s.product_id) ?? 0) + q)
      }

      // Mapa de última venda real por produto (best-effort — se a view falhar, encalhe fica false)
      const lastSaleMap = new Map<string, string>()
      for (const s of (lastSaleRes.data ?? []) as Array<{ product_id: string; last_sale_date: string }>) {
        lastSaleMap.set(s.product_id, s.last_sale_date)
      }
      const ENCALHE_DAYS = 90
      const encalheCutoff = Date.now() - ENCALHE_DAYS * 24 * 60 * 60 * 1000

      const activeByProduct = new Map<string, ActiveOrderInfo>()
      orders.forEach(o => {
        o.purchase_order_items.forEach(it => {
          if (it.product_id && !activeByProduct.has(it.product_id)) {
            activeByProduct.set(it.product_id, {
              order_number: o.order_number,
              created_at: o.created_at,
              status: o.status,
            })
          }
        })
      })

      setRows(products.map(p => {
        const open_qty = openQtyByProduct.get(p.id) ?? 0
        // Colchão anti-ruptura do item pela sua curva ABC (sem curva → tier C).
        const safety_days = resolveSafetyDays(p.abc_curve, safetyCfg)
        // buildProductRow já abate open_qty em gap_to_buy e purchase_suggestion,
        // e soma safety_days ao ponto de ressuprimento (restaura o colchão).
        const base = buildProductRow(p, sales, open_qty, safety_days)
        const active = activeByProduct.get(p.id)
        // Sinal preventivo: vai zerar antes da reposição (lead time + colchão) e não há
        // pedido em aberto que cubra. Fase 1: qualquer pedido em aberto conta como cobertura.
        // Fase 2: usa a cobertura na DEMANDA PREVISTA (reorder_coverage_days) — item
        // acelerando entra em risco mais cedo. O gate de demanda segue a média plana.
        const em_risco = isAtRuptureRisk({
          coverage_days: base.reorder_coverage_days,
          lead_time: p.suppliers?.lead_time_days ?? null,
          safety_days,
          daily_avg: base.daily_avg,
          has_covering_order: open_qty > 0,
        })
        // Encalhe: mesma régua do dashboard/inativos — saldo > 0 + venda real + >90 dias.
        // Usa a view (vendas puras), NÃO o last_sale_at do base (que tem fallback p/ movimentação).
        const pureLastSale = lastSaleMap.get(p.id) ?? null
        const encalhe = p.current_stock > 0 && !!pureLastSale &&
          new Date(pureLastSale).getTime() < encalheCutoff

        const purchase_suggestion = base.purchase_suggestion
        const filial_suggestion = fMap.get(p.sku)?.total ?? 0

        // Motor de compras: estoque máximo = mesma régua do Painel BX (Ponto de Ressuprimento).
        const estoque_max = base.reorder_point
        const l30 = last30.get(p.id) ?? 0
        const venda_crescente = l30 > 0 && l30 > (prev30.get(p.id) ?? 0)
        const { score } = computeCommercialScore({
          abc: p.abc_curve,
          current_stock: p.current_stock,
          kanban_status: p.kanban_status,
          margin_pct: p.margin_pct,
          venda_crescente,
          open_qty,
          coverage_days: base.coverage_days,
          lead_time: p.suppliers?.lead_time_days ?? 7,
          daily_avg: base.daily_avg,
        })
        // Status (chips) segue a régua de prioridade do Painel BX, não o score.
        const status = commercialStatusFromPriority(base.priority)

        return {
          ...base,
          purchase_suggestion,
          active_order_number: active?.order_number ?? null,
          active_order_date: active?.created_at ?? null,
          active_order_status: active?.status ?? null,
          em_risco,
          encalhe,
          filial_suggestion,
          total_suggestion: purchase_suggestion + filial_suggestion,
          open_qty,
          estoque_max,
          commercial_score: score,
          commercial_status: status,
        }
      }))
      setLoading(false)
    } catch (err) {
      setError((err as Error).message)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()

    // Realtime de `sales` e `products` foi removido: a sync do Tiny insere centenas de
    // vendas e mexe em ~900 produtos de uma vez, disparando refetch COMPLETO (produtos +
    // 365d de vendas) em cascata — egress alto, multiplicado por cada cliente conectado.
    // O usuário pode dar refresh manual (navegar/remontar) para ver dados novos do sync.
    // Mantemos só `purchase_orders` (volume baixo, dirigido pelo usuário) para a sugestão
    // refletir pedidos recém-emitidos. Debounce agrupa rajadas de itens num único refetch.
    let timer: ReturnType<typeof setTimeout> | null = null
    const scheduleRefetch = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(fetchData, 1500)
    }
    // Nome único por instância: o hook pode montar 2x na mesma página (ex.: Miguel
    // + a sub-aba Explorar). Canais Supabase com o MESMO tópico colidem no subscribe
    // e derrubam o componente — o sufixo aleatório evita a colisão.
    const channel = supabase
      .channel(`replenishment_realtime_${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'purchase_orders' }, scheduleRefetch)
      .subscribe()

    // Editar o colchão anti-ruptura (useCompanySafetyStock) recomputa o painel sem reload.
    window.addEventListener(REPLENISHMENT_REFRESH_EVENT, scheduleRefetch)

    return () => {
      if (timer) clearTimeout(timer)
      supabase.removeChannel(channel)
      window.removeEventListener(REPLENISHMENT_REFRESH_EVENT, scheduleRefetch)
    }
  }, [])

  return { rows, loading, error, hasFiliais, filialMap }
}
